import { useState, useEffect } from "react";
import { DashboardContainer } from "../base_components/dashboard_container";
import { trpc } from "../../../utils/trpc";
import { DashboardUtils } from "../../../dashboard_utils";
import { useHistory } from "react-router-dom";
import { SeshPrimaryButton } from "../base_components/sesh_primary_button";
import classNames from "classnames";

interface TestEntitlement {
	id: string;
	sku_id: string;
	guild_id: string;
	created_at: string;
}

export function AdminEntitlementsPage() {
	const is_admin_query = trpc.admin.isSeshAdmin.useQuery();
	const history = useHistory();

	const [guildId, setGuildId] = useState("");
	const [skuId, setSkuId] = useState("");
	const [isGranting, setIsGranting] = useState(false);
	const [error, setError] = useState<string | null>(null);
	const [success, setSuccess] = useState<string | null>(null);

	// Mock data for now - will be replaced with actual API calls
	const [testEntitlements, setTestEntitlements] = useState<TestEntitlement[]>([]);

	const grantTestEntitlementMutation = trpc.admin.grantTestEntitlement.useMutation({
		onSuccess: (data) => {
			setSuccess(`Successfully granted test entitlement to guild ${guildId}`);
			setGuildId("");
			setSkuId("");
			// Refetch entitlements
			refetchEntitlements();
		},
		onError: (error) => {
			setError(error.message || "Failed to grant test entitlement");
		}
	});

	const deleteTestEntitlementMutation = trpc.admin.deleteTestEntitlement.useMutation({
		onSuccess: () => {
			setSuccess("Successfully removed test entitlement");
			// Refetch entitlements
			refetchEntitlements();
		},
		onError: (error) => {
			setError(error.message || "Failed to remove test entitlement");
		}
	});

	const listTestEntitlementsQuery = trpc.admin.listTestEntitlements.useQuery(undefined, {
		onSuccess: (data) => {
			setTestEntitlements(data);
		}
	});

	const refetchEntitlements = () => {
		listTestEntitlementsQuery.refetch();
	};

	useEffect(() => {
		if (error || success) {
			const timer = setTimeout(() => {
				setError(null);
				setSuccess(null);
			}, 5000);
			return () => clearTimeout(timer);
		}
	}, [error, success]);

	if (is_admin_query.isError) {
		return (
			<DashboardUtils.RenderDashboardErrorPage
				text="Must be admin to view this page"
				title="UNAUTHORIZED"
			/>
		);
	}

	if (!is_admin_query.isSuccess) {
		return <DashboardUtils.RenderFullLoadingThrobberPage />;
	}

	const handleGrantEntitlement = async (e: React.FormEvent) => {
		e.preventDefault();
		if (!guildId.trim() || !skuId.trim()) {
			setError("Both Guild ID and SKU ID are required");
			return;
		}
		
		setIsGranting(true);
		try {
			await grantTestEntitlementMutation.mutateAsync({
				guild_id: guildId.trim(),
				sku_id: skuId.trim()
			});
		} finally {
			setIsGranting(false);
		}
	};

	const handleDeleteEntitlement = async (entitlementId: string) => {
		if (!window.confirm("Are you sure you want to remove this test entitlement?")) {
			return;
		}
		
		try {
			await deleteTestEntitlementMutation.mutateAsync({
				entitlement_id: entitlementId
			});
		} catch (err) {
			// Error handled in mutation callbacks
		}
	};

	return (
		<DashboardContainer title="Admin - Discord Entitlements">
			<button
				onClick={() => history.push("/dashboard/admin")}
				className="mb-6 text-blue-400 hover:text-blue-300 flex items-center gap-2"
			>
				<svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
					<path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
				</svg>
				Back to Admin Dashboard
			</button>

			{/* Notification Messages */}
			{error && (
				<div className="mb-6 p-4 bg-red-900 bg-opacity-50 border border-red-600 rounded-lg text-red-200">
					{error}
				</div>
			)}
			{success && (
				<div className="mb-6 p-4 bg-green-900 bg-opacity-50 border border-green-600 rounded-lg text-green-200">
					{success}
				</div>
			)}

			{/* Grant Test Entitlement Form */}
			<div className="bg-gray-800 rounded-lg p-6 mb-8">
				<h2 className="text-xl font-bold mb-4">Grant Test Entitlement</h2>
				<form onSubmit={handleGrantEntitlement} className="space-y-4">
					<div>
						<label htmlFor="guild-id" className="block text-sm font-medium mb-2">
							Guild ID
						</label>
						<input
							id="guild-id"
							type="text"
							value={guildId}
							onChange={(e) => setGuildId(e.target.value)}
							placeholder="Enter Discord Guild ID"
							className="w-full px-4 py-2 bg-gray-700 border border-gray-600 rounded-lg focus:outline-none focus:border-blue-400 text-white"
						/>
					</div>
					<div>
						<label htmlFor="sku-id" className="block text-sm font-medium mb-2">
							SKU ID
						</label>
						<input
							id="sku-id"
							type="text"
							value={skuId}
							onChange={(e) => setSkuId(e.target.value)}
							placeholder="Enter Discord SKU ID"
							className="w-full px-4 py-2 bg-gray-700 border border-gray-600 rounded-lg focus:outline-none focus:border-blue-400 text-white"
						/>
					</div>
					<SeshPrimaryButton
						type="submit"
						disabled={isGranting || !guildId.trim() || !skuId.trim()}
						is_loading={isGranting}
					>
						{isGranting ? "Granting..." : "Grant Entitlement"}
					</SeshPrimaryButton>
				</form>
			</div>

			{/* Test Entitlements List */}
			<div className="bg-gray-800 rounded-lg p-6">
				<h2 className="text-xl font-bold mb-4">Test Entitlements</h2>
				
				{listTestEntitlementsQuery.isLoading ? (
					<div className="text-center py-8">
						<div className="text-gray-400">Loading entitlements...</div>
					</div>
				) : testEntitlements.length === 0 ? (
					<div className="text-center py-8 text-gray-400">
						No test entitlements found
					</div>
				) : (
					<div className="space-y-3">
						{testEntitlements.map((entitlement) => (
							<div
								key={entitlement.id}
								className="bg-gray-700 rounded-lg p-4 flex items-center justify-between"
							>
								<div>
									<div className="font-medium">
										Guild ID: <span className="text-blue-400">{entitlement.guild_id}</span>
									</div>
									<div className="text-sm text-gray-400 mt-1">
										SKU ID: {entitlement.sku_id}
									</div>
									<div className="text-sm text-gray-400 mt-1">
										Created: {new Date(entitlement.created_at).toLocaleString()}
									</div>
								</div>
								<button
									onClick={() => handleDeleteEntitlement(entitlement.id)}
									className="px-4 py-2 bg-red-600 hover:bg-red-700 rounded-lg transition-colors"
									disabled={deleteTestEntitlementMutation.isLoading}
								>
									Remove
								</button>
							</div>
						))}
					</div>
				)}
			</div>
		</DashboardContainer>
	);
}